import './LandingHeader.scss';

import { MicrositeHeroBlock } from '@travelwin/cms/types';
import { Body, ButtonV2, Country, Title } from '@travelwin/core';
import classNames from 'classnames';

import { ImageMedia } from '@/components/ImageMedia';
import { Link } from '@/components/Link';
import { serializeLexicalChildNode } from '@/components/RichText/serialize-lexical';
import { getCmsBaseUrl, getCmsMediaBaseUrl } from '@/utils/ssrUtils';

import { RatingWidget } from '../../../../components/RatingWidget';
import SearchSection from './LandingSearch';

const className = 'c-LandingHeader';

const getBackgroundImageUrls = (
  media: MicrositeHeroBlock['backgroundImage'],
) => {
  if (typeof media === 'number') {
    return [];
  }

  const cmsMediaBaseUrl = getCmsMediaBaseUrl();
  const mediaBaseUrl = cmsMediaBaseUrl ?? getCmsBaseUrl();

  const backgroundImageUrls = cmsMediaBaseUrl
    ? [
        `${mediaBaseUrl}/${media.sizes?.w480?.filename}`,
        `${mediaBaseUrl}/${media.sizes?.w768?.filename}`,
      ]
    : [
        `${mediaBaseUrl}/${media.sizes?.w480?.url}`,
        `${mediaBaseUrl}/${media.sizes?.w768?.url}`,
      ];

  return backgroundImageUrls;
};

export default function MicrositeLandingHeader({
  countries,
  content,
}: {
  countries: Country[];
  content: MicrositeHeroBlock;
}) {
  const [mobileBackgroundUrl, desktopBackgroundUrl] = getBackgroundImageUrls(
    content.backgroundImage,
  );

  const customStyle = {
    '--bg-image-url-mobile': `url(${mobileBackgroundUrl})`,
    '--bg-image-url-desktop': `url(${desktopBackgroundUrl})`,
  } as React.CSSProperties;

  return (
    <div className={className} style={customStyle}>
      <div className={`${className}__container flex flex-column gap-24`}>
        <div className={`${className}__logos`}>
          <ImageMedia media={content.partnerLogo} sizes="300px" />
        </div>
        <div className={classNames('flex flex-column gap-24')}>
          <Title tag={1} data-testid="title-landing-page">
            {serializeLexicalChildNode({ content: content.title })}
          </Title>
          <Body>{serializeLexicalChildNode({ content: content.text })}</Body>
          {content.withCountrySearch ? (
            <SearchSection countries={countries} />
          ) : null}

          <RatingWidget align="left-tablet-up" />

          <div>
            {content.primaryCta?.title && content.primaryCta?.href ? (
              <ButtonV2
                variant="primary"
                intent="support-violet"
                asChild
                size={{ sm: 'medium', md: 'medium', lg: 'huge' }}
              >
                <Link
                  to={content.primaryCta.href}
                  target={content.primaryCta.newTab ? '_blank' : undefined}
                >
                  <Body accent="title" color="white">
                    {content.primaryCta.title}
                  </Body>
                </Link>
              </ButtonV2>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
